import * as z from 'zod/v4';

import { drawNumberSchema, drawStateSchema } from '../draw/entry.js';
import { currencySchema, winDivisionSchema } from '../lib/entry.js';
import { eventNumberSchema, eventTypeIdSchema, matchStateSchema } from '../sport/entry.js';
import { outcomeSchema } from './outcomeSchema.js';
import { scoreSchema } from './scoreSchema.js';

const cancelledSchema = z.number().transform((number) => number > 0);

const drawForecastResultSchema = z
  .object({
    eventNumber: eventNumberSchema,
    eventTypeId: eventTypeIdSchema,
    cancelled: cancelledSchema,
    matchResultState: matchStateSchema,
  })
  .and(
    outcomeSchema.transform((outcome) => ({
      outcome,
    }))
  )
  .and(
    scoreSchema.transform((result) => ({
      result,
    }))
  )
  .transform(({ matchResultState: matchResult, eventTypeId: eventType, cancelled: isCancelled, ...schema }) => ({
    ...schema,
    eventType,
    matchResult,
    isCancelled,
  }));

export type DrawEventResult = z.infer<typeof drawForecastResultSchema>;

const winResultSchema = z
  .object({
    winDiv: winDivisionSchema,
    winners: z.number(),
    winValue: currencySchema,
  })
  .transform(({ winDiv: winDivision, ...schema }) => ({ ...schema, winDivision }));

export type WinDivisions = z.infer<typeof winResultSchema>;

const multiForecastSchema = z
  .object({
    winDiv: winDivisionSchema,
    winners: z.number(),
    winValue: currencySchema,
    eventNumber: eventNumberSchema,
    outcome: z.string(),
  })
  .transform(({ winDiv: winDivision, ...schema }) => ({ ...schema, winDivision }));

export type MultiForecast = z.infer<typeof multiForecastSchema>;

export const drawForecastSchema = z
  .object({
    drawnum: drawNumberSchema,
    drawState: drawStateSchema,
    cancelled: cancelledSchema,
    drawResults: z.array(drawForecastResultSchema),
    totalSales: currencySchema,
    winresult: z.array(winResultSchema),
    multiForecast: z.array(multiForecastSchema).optional().catch(undefined),
    // Properties that does not exist on draw result that might need to be handled in somehow (maybe discriminated unions?)
    // resultIdx
    // ref_resultIdx
    // totNumPartRows
    // numBoards
    // numPartRows
  })
  .transform(
    ({ drawnum: drawNumber, totalSales: netSale, winresult: winDivisions, cancelled: isCancelled, ...schema }) => ({
      ...schema,
      drawNumber,
      netSale,
      winDivisions,
      isCancelled,
    })
  );

export type DrawForecast = z.infer<typeof drawForecastSchema>;
